// Copyright (C) 2020-2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import { BoundariesActions, BoundariesActionTypes } from 'actions/boundaries-actions';
import { AuthActions, AuthActionTypes } from 'actions/auth-actions';
import { ShortcutsActions, ShortcutsActionsTypes } from 'actions/shortcuts-actions';
import { KeyMap, KeyMapItem } from 'utils/mousetrap-react';
import { DimensionType } from 'cvat-core-wrapper';
import { ShortcutsState } from '.';

function formatShortcuts(shortcuts: KeyMapItem): string {
    const list: string[] = shortcuts.displayedSequences || (shortcuts.sequences as string[]);
    return `[${list
        .map((shortcut: string): string => {
            let keys = shortcut.split('+');
            keys = keys.map((key: string): string => `${key ? key[0].toUpperCase() : key}${key.slice(1)}`);
            keys = keys.join('+').split(/\s/g);
            keys = keys.map((key: string): string => `${key ? key[0].toUpperCase() : key}${key.slice(1)}`);
            return keys.join(' ');
        })
        .join(', ')}]`;
}

const defaultKeyMap = ({
    SWITCH_SHORTCUTS: {
        name: '查看快捷键列表',
        description: '显示或隐藏可用的快捷键列表',
        sequences: ['f1'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SWITCH_SETTINGS: {
        name: '查看设置',
        description: '显示或隐藏设置面板',
        sequences: ['f2'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },

    SWITCH_ALL_LOCK: {
        name: '锁定或解锁所有标注',
        description: '修改侧边栏中所有标注的锁定状态',
        sequences: ['t l'], // 先按 t 再按 l
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SWITCH_LOCK: {
        name: '锁定或解锁标注',
        description: '修改当前标注的锁定状态',
        sequences: ['l'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SWITCH_ALL_HIDDEN: {
        name: '显示或隐藏所有标注',
        description: '切换侧边栏中标注的隐藏状态',
        sequences: ['t h'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SWITCH_HIDDEN: {
        name: '显示或隐藏当前标注',
        description: '切换当前标注的隐藏状态',
        sequences: ['h'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SWITCH_OCCLUDED: {
        name: '是否有遮挡',
        description: '修改当前标注的是否有遮挡属性',
        sequences: ['q', '/'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SWITCH_KEYFRAME: {
        name: '是否是关键帧',
        description: '修改当前追踪序列的关键帧属性',
        sequences: ['k'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    SWITCH_OUTSIDE: {
        name: '是否可见',
        description: '修改标注是否在当前追踪序列是否可见的状态',
        sequences: ['o'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    DELETE_OBJECT: {
        name: '删除标注',
        description: '删除当前选中的标注',
        sequences: ['shift+r', 'r'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    DETECT_OBJECTS: {
        name: '',
        descrption: '使用检测模型检测出当前帧中的所有目标',
        sequences: ['shift+b'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    TO_BACKGROUND: {
        name: '置于底层',
        description: '让标注对象所在的图层更深',
        sequences: ['-', '_'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    TO_FOREGROUND: {
        name: '置于顶层',
        description: '让标注对象所在的图层更浅',
        sequences: ['+', '='],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    PROPAGATE_OBJECT: {
        name: '派生形状',
        description: '将当前形状派生到后续帧中',
        sequences: ['ctrl+b'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    NEXT_KEY_FRAME: {
        name: '一个关键帧',
        description: '跳转到当前追踪序列中的下一个关键帧',
        sequences: ['e'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    PREV_KEY_FRAME: {
        name: '上一个关键帧',
        description: '跳转到当前追踪序列中的上一个关键帧',
        sequences: ['w'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },

    NEXT_ATTRIBUTE: {
        name: '下一个属性',
        description: '跳转到下一个属性',
        sequences: ['down'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    PREVIOUS_ATTRIBUTE: {
        name: '上一个属性',
        description: '跳转到上一个属性',
        sequences: ['up'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    NEXT_OBJECT: {
        name: '下一个',
        description: '跳转到下一个标注',
        sequences: ['tab'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    PREVIOUS_OBJECT: {
        name: '上一个标注对象',
        description: '跳转到上一个标注',
        sequences: ['shift+tab'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },

    COPY_SHAPE: {
        name: '拷贝形状',
        description: '将形状拷贝到粘贴板中',
        sequences: ['ctrl+c'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    PASTE_SHAPE: {
        name: '粘贴形状',
        description: '从粘贴板中粘贴形状',
        sequences: ['ctrl+v'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SWITCH_DRAW_MODE: {
        name: '开始或结束标注模式',
        description:
            '在当前工作空间使用上一次的形状和参数绘制标注 (使用 shift 重新绘制当前形状)',
        sequences: ['shift+n', 'n'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    OPEN_REVIEW_ISSUE: {
        name: '创建一个审核问题',
        description: '在审核工作空间创建一个新的问题',
        sequences: ['n'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    SWITCH_MERGE_MODE: {
        name: '开始或结束合并模式',
        description: '开始或结束合并形状',
        sequences: ['m'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    SWITCH_SPLIT_MODE: {
        name: '开始或结束分裂模式',
        description: '开始或结束分裂形状',
        sequences: ['alt+m'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    SWITCH_GROUP_MODE: {
        name: '开始或结束分组模式',
        description: '进入或退出分组模式',
        sequences: ['g'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    RESET_GROUP: {
        name: '重置分组',
        description: '取消所选中的标注在同一分组中（在分组模式下）',
        sequences: ['shift+g'],
        action: 'keyup',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    CANCEL: {
        name: '退出 标注或分组或合并或分裂 模式',
        description: '退出 标注或分组或合并或分裂 模式',
        sequences: ['esc'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    CLOCKWISE_ROTATION: {
        name: '顺时针旋转 90°',
        description: '顺时针旋转 90°',
        sequences: ['ctrl+r'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    ANTICLOCKWISE_ROTATION: {
        name: '逆时针旋转 90°',
        description: '逆时针旋转 90°',
        sequences: ['ctrl+shift+r'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },

    SAVE_JOB: {
        name: '保存作业',
        description: '将当前作业的所有标注都保存到服务器',
        sequences: ['ctrl+s'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    UNDO: {
        name: '撤销上一次操作',
        description: '取消与标注相关的上一次操作',
        sequences: ['ctrl+z'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    REDO: {
        name: '回退上一次操作',
        description: '取消撤销上一次操作',
        sequences: ['ctrl+shift+z', 'ctrl+y'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    DELETE_FRAME: {
        name: '删除当前帧',
        description: '删除当前帧',
        sequences: ['alt+del'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    NEXT_FRAME: {
        name: '下一帧',
        description: '跳转到下一帧',
        sequences: ['f'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    PREV_FRAME: {
        name: '前一帧',
        description: '跳转到上一帧',
        sequences: ['d'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    FORWARD_FRAME: {
        name: '使用步长向前跳转',
        description: '使用步长向前跳转',
        sequences: ['v'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    BACKWARD_FRAME: {
        name: '使用步长向后跳转',
        description: '使用步长向后跳转',
        sequences: ['c'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SEARCH_FORWARD: {
        name: '在当前过滤规则下向后跳转',
        description: '寻找满足当前过滤条件的下一帧',
        sequences: ['right'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SEARCH_BACKWARD: {
        name: '在当前过滤规则下向前跳转',
        description: '寻找满足当前过滤条件的上一帧',
        sequences: ['left'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    PLAY_PAUSE: {
        name: '播放或暂停',
        description: '开始或停止自动改变帧',
        sequences: ['p'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    FOCUS_INPUT_FRAME: {
        name: '快速聚焦到帧数跳转的输入框',
        description: '快速聚焦到帧数跳转的输入框',
        sequences: ['`'],
        displayedSequences: ['~'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SWITCH_TOOLS_BLOCKER_STATE: {
        name: '延迟模型调用',
        description: '在交互模式下，延迟模型调用',
        sequences: ['ctrl'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    TOGGLE_LAYOUT_GRID: {
        name: '显示或隐藏布局网格',
        description: '显示或隐藏用于辅助 UI 开发的网格',
        sequences: ['ctrl+alt+enter'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D],
    },
    SWITCH_LABEL: {
        name: '修改标签',
        description: '修改当前标注的标签，或者当没有标注被选中时修改后续标注的标签',
        sequences: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].map((val: string): string => `space+${val}`),
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    SWITCH_ATTRIBUTE_DISPLAY: {
        name: '切换属性显示',
        description: '切换非选中标注的属性是否显示',
        sequences: ['m'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_2D, DimensionType.DIMENSION_3D],
    },
    TILT_UP: {
        name: 'Camera Roll Angle Up',
        description: 'Increases camera roll angle',
        sequences: ['shift+arrowup'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
    TILT_DOWN: {
        name: 'Camera Roll Angle Down',
        description: 'Decreases camera roll angle',
        sequences: ['shift+arrowdown'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
    ROTATE_LEFT: {
        name: 'Camera Pitch Angle Left',
        description: 'Decreases camera pitch angle',
        sequences: ['shift+arrowleft'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
    ROTATE_RIGHT: {
        name: 'Camera Pitch Angle Right',
        description: 'Increases camera pitch angle',
        sequences: ['shift+arrowright'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
    MOVE_UP: {
        name: 'Camera Move Up',
        description: 'Move the camera up',
        sequences: ['alt+u'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
    MOVE_DOWN: {
        name: 'Camera Move Down',
        description: 'Move the camera down',
        sequences: ['alt+o'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
    MOVE_LEFT: {
        name: 'Camera Move Left',
        description: 'Move the camera left',
        sequences: ['alt+j'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
    MOVE_RIGHT: {
        name: 'Camera Move Right',
        description: 'Move the camera right',
        sequences: ['alt+l'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
    ZOOM_IN: {
        name: 'Camera Zoom In',
        description: 'Performs zoom in',
        sequences: ['alt+i'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
    ZOOM_OUT: {
        name: 'Camera Zoom Out',
        description: 'Performs zoom out',
        sequences: ['alt+k'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
    CANCEL_SKELETON_EDGE: {
        name: 'Cancel skeleton drawing',
        description: 'Interrupts drawing a new skeleton edge',
        sequences: ['esc'],
        action: 'keydown',
        applicable: [DimensionType.DIMENSION_3D],
    },
} as any) as KeyMap;

const defaultState: ShortcutsState = {
    visibleShortcutsHelp: false,
    keyMap: defaultKeyMap,
    normalizedKeyMap: Object.keys(defaultKeyMap).reduce((acc: Record<string, string>, key: string) => {
        const normalized = formatShortcuts(defaultKeyMap[key]);
        acc[key] = normalized;
        return acc;
    }, {}),
};

export default (state = defaultState, action: ShortcutsActions | BoundariesActions | AuthActions): ShortcutsState => {
    switch (action.type) {
        case ShortcutsActionsTypes.SWITCH_SHORTCUT_DIALOG: {
            return {
                ...state,
                visibleShortcutsHelp: action.payload.visible,
            };
        }
        case BoundariesActionTypes.RESET_AFTER_ERROR:
        case AuthActionTypes.LOGOUT_SUCCESS: {
            return { ...defaultState };
        }
        default: {
            return state;
        }
    }
};
